import React, { useState, useEffect } from 'react';
import { Trash2, AlertTriangle, CheckCircle, XCircle, Loader2, RefreshCw } from 'lucide-react';
import { urls } from './config.dev';
import TestResultModal from './TestResultModal';
import { useNavigate } from 'react-router-dom';
import Topbar from './Topbar';

// ─── DESIGN TOKENS ───────────────────────────────────────────────────────────
const colors = {
  // Blue theme - Dark theme with vibrant accents
  blue: {
    sidebarBg: '#0a1e4a',
    sidebarBorder: '#1e3a8a',
    activeNavBg: '#2563eb',
    activeNavText: '#ffffff',
    inactiveNavText: '#e0e7ff',
    navHoverBg: '#1e3a8a',
    sectionHeaderText: '#94a3b8',
    mainBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    accent: '#16a34a',
    accentLight: '#dcfce7',
    danger: '#dc2626',
    dangerLight: '#fef2f2',
    warning: '#d97706',
    warningLight: '#fffbeb',
    info: '#2563eb',
    infoLight: '#eff6ff',
    skyBlue: '#38bdf8',
    skyBlueLight: '#e0f2fe',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    tableHeader: '#f1f5f9',
    tableBorder: '#e2e8f0',
    badgeGreen: { bg: '#dcfce7', text: '#166534' },
    badgeRed: { bg: '#fee2e2', text: '#991b1b' },
    badgeOrange: { bg: '#ffedd5', text: '#9a3412' },
    badgeBlue: { bg: '#dbeafe', text: '#1e40af' },
    badgeSky: { bg: '#e0f2fe', text: '#0369a1' },
    badgeGray: { bg: '#f1f5f9', text: '#475569' },
    filterSection: '#0d2257',
    collapseButtonBg: '#1e3a8a',
    collapseButtonHover: '#2563eb',
    collapseButtonText: '#ffffff',
    tooltipBg: '#1e293b',
    tooltipText: '#ffffff',
    iconBright: '#fbbf24',
    iconHover: '#f59e0b',
    sidebarText: '#ffffff',
    sidebarTextMuted: '#a5b4fc',
    staffCardBg: 'rgba(255,255,255,0.05)',
    staffCardBorder: '#1e3a8a',
    logoBg: '#2563eb',
  },
  // White theme - Light theme with dark text
  white: {
    sidebarBg: '#ffffff',
    sidebarBorder: '#e2e8f0',
    activeNavBg: '#f1f5f9',
    activeNavText: '#0f172a',
    inactiveNavText: '#475569',
    navHoverBg: '#f8fafc',
    sectionHeaderText: '#64748b',
    mainBg: '#f8fafc',
    cardBg: '#ffffff',
    cardBorder: '#e2e8f0',
    accent: '#16a34a',
    accentLight: '#dcfce7',
    danger: '#dc2626',
    dangerLight: '#fef2f2',
    warning: '#d97706',
    warningLight: '#fffbeb',
    info: '#2563eb',
    infoLight: '#eff6ff',
    skyBlue: '#38bdf8',
    skyBlueLight: '#e0f2fe',
    textPrimary: '#0f172a',
    textSecondary: '#475569',
    textMuted: '#64748b',
    tableHeader: '#f1f5f9',
    tableBorder: '#e2e8f0',
    badgeGreen: { bg: '#dcfce7', text: '#166534' },
    badgeRed: { bg: '#fee2e2', text: '#991b1b' },
    badgeOrange: { bg: '#ffedd5', text: '#9a3412' },
    badgeBlue: { bg: '#dbeafe', text: '#1e40af' },
    badgeSky: { bg: '#e0f2fe', text: '#0369a1' },
    badgeGray: { bg: '#f1f5f9', text: '#475569' },
    filterSection: '#f8fafc',
    collapseButtonBg: '#e2e8f0',
    collapseButtonHover: '#cbd5e1',
    collapseButtonText: '#0f172a',
    tooltipBg: '#1e293b',
    tooltipText: '#ffffff',
    iconBright: '#f59e0b',
    iconHover: '#d97706',
    sidebarText: '#0f172a',
    sidebarTextMuted: '#64748b',
    staffCardBg: '#f8fafc',
    staffCardBorder: '#e2e8f0',
    logoBg: '#2563eb',
  }
};

// ─── Global Styles Component ─────────────────────────────────────────────────
const GlobalStyles = ({ theme }) => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700&display=swap');
    
    * { 
      box-sizing: border-box; 
      margin: 0; 
      padding: 0; 
      font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    }
    
    ::-webkit-scrollbar { width: 6px; height: 6px; }
    ::-webkit-scrollbar-track { background: ${theme.tableHeader}; }
    ::-webkit-scrollbar-thumb { background: ${theme.textMuted}; border-radius: 4px; }
    ::-webkit-scrollbar-thumb:hover { background: ${theme.textSecondary}; }
    
    @keyframes spin {
      from { transform: rotate(0deg); }
      to { transform: rotate(360deg); }
    }
    .spin {
      animation: spin 1s linear infinite;
    }
    
    .fade-in { 
      animation: fadeIn 0.3s ease; 
    }
    
    @keyframes fadeIn { 
      from { opacity: 0; transform: translateY(6px); } 
      to { opacity: 1; transform: translateY(0); } 
    }
    
    @media (max-width: 768px) {
      .lab-grid {
        grid-template-columns: 1fr !important;
      }
    }

    /* ── Empty State Lab Animation ───────────────────────────────────── */
    @keyframes labFloat {
      0%, 100% { transform: translateY(0px); }
      50%       { transform: translateY(-10px); }
    }

    @keyframes labBobble {
      0%, 100% { transform: translateY(0px) rotate(-2deg); }
      50%       { transform: translateY(-8px) rotate(2deg); }
    }

    @keyframes bubbleRise {
      0%   { opacity: 0;   transform: translateY(0) scale(0.6); }
      30%  { opacity: 0.9; }
      100% { opacity: 0;   transform: translateY(-44px) scale(1.1); }
    }

    @keyframes liquidSlosh {
      0%, 100% { d: path("M0 22 Q20 18 40 22 Q60 26 80 22 L80 50 L0 50 Z"); }
      50%       { d: path("M0 26 Q20 22 40 26 Q60 30 80 26 L80 50 L0 50 Z"); }
    }

    @keyframes sparkle {
      0%, 100% { opacity: 0;   transform: scale(0) rotate(0deg); }
      50%       { opacity: 1;   transform: scale(1) rotate(180deg); }
    }

    @keyframes steamRise {
      0%   { opacity: 0;   transform: translateY(0) scaleX(1); }
      40%  { opacity: 0.6; transform: translateY(-12px) scaleX(1.3); }
      100% { opacity: 0;   transform: translateY(-28px) scaleX(0.8); }
    }

    @keyframes emptyPulse {
      0%, 100% { opacity: 1; }
      50%       { opacity: 0.6; }
    }

    .lab-empty-flask   { animation: labFloat  3.2s ease-in-out infinite; }
    .lab-empty-beaker  { animation: labBobble 3.8s ease-in-out infinite 0.4s; }

    .lab-bubble-1 { animation: bubbleRise 2.4s ease-in-out infinite 0.0s; }
    .lab-bubble-2 { animation: bubbleRise 2.4s ease-in-out infinite 0.8s; }
    .lab-bubble-3 { animation: bubbleRise 2.4s ease-in-out infinite 1.6s; }
    .lab-bubble-b1 { animation: bubbleRise 2.8s ease-in-out infinite 0.3s; }
    .lab-bubble-b2 { animation: bubbleRise 2.8s ease-in-out infinite 1.2s; }

    .lab-sparkle-1 { animation: sparkle 2.0s ease-in-out infinite 0.0s; }
    .lab-sparkle-2 { animation: sparkle 2.0s ease-in-out infinite 0.7s; }
    .lab-sparkle-3 { animation: sparkle 2.0s ease-in-out infinite 1.4s; }

    .lab-steam-1 { animation: steamRise 2.2s ease-in-out infinite 0.0s; }
    .lab-steam-2 { animation: steamRise 2.2s ease-in-out infinite 0.7s; }
    .lab-steam-3 { animation: steamRise 2.2s ease-in-out infinite 1.4s; }

    .lab-empty-text { animation: emptyPulse 3s ease-in-out infinite; }
  `}</style>
);

// ─── Empty State Animation Component ─────────────────────────────────────────
const LabEmptyState = ({ theme }) => (
  <div style={{
    textAlign: 'center',
    marginTop: '60px',
    backgroundColor: theme.cardBg,
    padding: '48px 40px',
    borderRadius: '20px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
    border: `1px solid ${theme.cardBorder}`,
    display: 'inline-block',
    maxWidth: '420px',
    width: '100%',
    margin: '60px auto 0',
  }}>
    {/* SVG illustration */}
    <svg
      viewBox="0 0 260 160"
      width="260"
      height="160"
      style={{ display: 'block', margin: '0 auto 24px', overflow: 'visible' }}
    >
      <defs>
        {/* Flask liquid gradient - green */}
        <linearGradient id="flaskLiquid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#4ade80" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#16a34a" stopOpacity="1" />
        </linearGradient>
        {/* Beaker liquid gradient - sky blue */}
        <linearGradient id="beakerLiquid" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#38bdf8" stopOpacity="0.9" />
          <stop offset="100%" stopColor="#0284c7" stopOpacity="1" />
        </linearGradient>
        {/* Glass gradient - subtle shine */}
        <linearGradient id="glassShine" x1="0" y1="0" x2="1" y2="0">
          <stop offset="0%" stopColor="#ffffff" stopOpacity="0.35" />
          <stop offset="40%" stopColor="#ffffff" stopOpacity="0.05" />
          <stop offset="100%" stopColor="#ffffff" stopOpacity="0.0" />
        </linearGradient>
        {/* Clip paths */}
        <clipPath id="flaskClip">
          <path d="M100 20 L100 52 L68 110 Q62 122 72 128 L128 128 Q138 122 132 110 L100 52 L100 20 Z" />
        </clipPath>
        <clipPath id="beakerClip">
          <rect x="155" y="42" width="62" height="86" rx="3" />
        </clipPath>
      </defs>

      {/* ── Conical Flask (left-center) ── */}
      <g className="lab-empty-flask" style={{ transformOrigin: '100px 80px' }}>
        {/* Flask body outline */}
        <path
          d="M100 18 L100 52 L67 111 Q60 126 72 132 L128 132 Q140 126 133 111 L100 52 L100 18"
          fill="none"
          stroke={theme.info}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {/* Flask glass body fill */}
        <path
          d="M100 20 L100 52 L68 110 Q62 122 72 128 L128 128 Q138 122 132 110 L100 52 L100 20 Z"
          fill={theme.infoLight}
          fillOpacity="0.5"
        />
        {/* Flask liquid */}
        <g clipPath="url(#flaskClip)">
          <rect x="60" y="98" width="80" height="34" fill="url(#flaskLiquid)" />
          {/* Liquid surface wave */}
          <ellipse cx="100" cy="98" rx="28" ry="5" fill="#4ade80" fillOpacity="0.7" />
        </g>
        {/* Flask glass shine */}
        <path
          d="M76 80 Q80 90 74 110"
          fill="none"
          stroke="#ffffff"
          strokeWidth="3"
          strokeOpacity="0.6"
          strokeLinecap="round"
        />
        {/* Flask neck */}
        <rect x="94" y="10" width="12" height="12" rx="2" fill={theme.infoLight} fillOpacity="0.6" stroke={theme.info} strokeWidth="2" />
        {/* Flask stopper */}
        <rect x="90" y="7" width="20" height="6" rx="3" fill={theme.info} />

        {/* Flask bubbles */}
        <circle className="lab-bubble-1" cx="90" cy="108" r="3.5" fill="#4ade80" fillOpacity="0.8" />
        <circle className="lab-bubble-2" cx="106" cy="112" r="2.5" fill="#86efac" fillOpacity="0.9" />
        <circle className="lab-bubble-3" cx="98" cy="105" r="2"   fill="#4ade80" fillOpacity="0.7" />
      </g>

      {/* ── Beaker (right-center) ── */}
      <g className="lab-empty-beaker" style={{ transformOrigin: '186px 88px' }}>
        {/* Beaker glass fill */}
        <rect x="155" y="42" width="62" height="88" rx="3" fill={theme.skyBlueLight} fillOpacity="0.5" />
        {/* Beaker outline */}
        <path
          d="M155 42 L155 130 Q155 134 159 134 L213 134 Q217 134 217 130 L217 42"
          fill="none"
          stroke={theme.skyBlue}
          strokeWidth="2.5"
          strokeLinejoin="round"
        />
        {/* Beaker rim / top */}
        <path
          d="M152 42 Q152 38 156 38 L214 38 Q218 38 218 42"
          fill="none"
          stroke={theme.skyBlue}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        {/* Beaker spout */}
        <path
          d="M213 42 Q218 36 222 34"
          fill="none"
          stroke={theme.skyBlue}
          strokeWidth="2.5"
          strokeLinecap="round"
        />
        {/* Liquid */}
        <g clipPath="url(#beakerClip)">
          <rect x="155" y="96" width="62" height="38" fill="url(#beakerLiquid)" />
          <ellipse cx="186" cy="96" rx="28" ry="4" fill="#38bdf8" fillOpacity="0.7" />
        </g>
        {/* Measurement lines */}
        <line x1="218" y1="80" x2="213" y2="80" stroke={theme.skyBlue} strokeWidth="1.5" />
        <line x1="218" y1="65" x2="211" y2="65" stroke={theme.skyBlue} strokeWidth="1.5" />
        <line x1="218" y1="112" x2="213" y2="112" stroke={theme.skyBlue} strokeWidth="1.5" />
        {/* Beaker glass shine */}
        <line x1="162" y1="50" x2="162" y2="126" stroke="#ffffff" strokeWidth="3" strokeOpacity="0.5" strokeLinecap="round" />

        {/* Beaker bubbles */}
        <circle className="lab-bubble-b1" cx="175" cy="108" r="3"   fill="#38bdf8" fillOpacity="0.85" />
        <circle className="lab-bubble-b2" cx="192" cy="112" r="2.5" fill="#7dd3fc" fillOpacity="0.9" />
      </g>

      {/* ── Steam from beaker ── */}
      <g style={{ transformOrigin: '186px 38px' }}>
        <ellipse className="lab-steam-1" cx="176" cy="34" rx="4" ry="3" fill={theme.skyBlue} fillOpacity="0.4" />
        <ellipse className="lab-steam-2" cx="186" cy="30" rx="3" ry="2.5" fill={theme.skyBlue} fillOpacity="0.35" />
        <ellipse className="lab-steam-3" cx="196" cy="34" rx="4" ry="3" fill={theme.skyBlue} fillOpacity="0.4" />
      </g>

      {/* ── Sparkles around both ── */}
      <g>
        {/* Sparkle 1 - top left of flask */}
        <g className="lab-sparkle-1" style={{ transformOrigin: '70px 50px' }}>
          <line x1="70" y1="46" x2="70" y2="54" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
          <line x1="66" y1="50" x2="74" y2="50" stroke="#fbbf24" strokeWidth="2" strokeLinecap="round" />
          <line x1="67.2" y1="47.2" x2="72.8" y2="52.8" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="72.8" y1="47.2" x2="67.2" y2="52.8" stroke="#fbbf24" strokeWidth="1.5" strokeLinecap="round" />
        </g>
        {/* Sparkle 2 - right of beaker */}
        <g className="lab-sparkle-2" style={{ transformOrigin: '232px 70px' }}>
          <line x1="232" y1="66" x2="232" y2="74" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" />
          <line x1="228" y1="70" x2="236" y2="70" stroke="#a78bfa" strokeWidth="2" strokeLinecap="round" />
        </g>
        {/* Sparkle 3 - between them */}
        <g className="lab-sparkle-3" style={{ transformOrigin: '140px 90px' }}>
          <line x1="140" y1="86" x2="140" y2="94" stroke="#f472b6" strokeWidth="2" strokeLinecap="round" />
          <line x1="136" y1="90" x2="144" y2="90" stroke="#f472b6" strokeWidth="2" strokeLinecap="round" />
          <line x1="137.2" y1="87.2" x2="142.8" y2="92.8" stroke="#f472b6" strokeWidth="1.5" strokeLinecap="round" />
          <line x1="142.8" y1="87.2" x2="137.2" y2="92.8" stroke="#f472b6" strokeWidth="1.5" strokeLinecap="round" />
        </g>
      </g>

      {/* ── Bench / shadow ── */}
      <ellipse cx="100" cy="144" rx="38" ry="6" fill={theme.tableBorder} fillOpacity="0.6" />
      <ellipse cx="186" cy="144" rx="40" ry="6" fill={theme.tableBorder} fillOpacity="0.6" />
    </svg>

    {/* Text */}
    <p
      className="lab-empty-text"
      style={{
        color: theme.textMuted,
        fontSize: '17px',
        fontWeight: '600',
        marginBottom: '6px',
      }}
    >
      All quiet in the lab!
    </p>
    <p style={{ color: theme.textMuted, fontSize: '13px', opacity: 0.75 }}>
      No pending tests right now
    </p>
  </div>
);

function Laboratory() {
  const [pendingLabTests, setPendingLabTests] = useState([]);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [clinicDetails, setClinicDetails] = useState({});
  const [clinicSettings, setClinicSettings] = useState({
    use_drug_expiry_date: 'no',
    use_drug_batch_numbers: 'no'
  });
  const [confirmDialog, setConfirmDialog] = useState({ 
    show: false, 
    type: '', 
    data: null,
    step: 1 // 1: initial confirmation, 2: sales record, 3: reason selection
  });
  const [confirmText, setConfirmText] = useState('');
  const [deleteSalesRecord, setDeleteSalesRecord] = useState(false);
  const [deleteReason, setDeleteReason] = useState('');
  const [token, setToken] = useState('');
  const [totalLabTests, setTotalLabTests] = useState(0);
  const [notification, setNotification] = useState({ show: false, type: '', message: '' });
  const [loadingStates, setLoadingStates] = useState({
    fetchClinic: false,
    fetchTests: false,
    deleteTest: false,
    deleteCard: false,
    refreshing: false
  });
  const [actionInProgress, setActionInProgress] = useState(null);
  const [currentTheme, setCurrentTheme] = useState('blue'); // default theme

  const navigate = useNavigate();
  const params = new URLSearchParams(window.location.search);
  const urlToken = params.get('token');

  // Get the active theme colors
  const theme = colors[currentTheme];

  // Reason options for different deletion types
  const testReasons = [
    "The sample collection has failed and we decided on another alternative so we returned the patient's money",
    "This test was ordered by mistake",
    "We have not been able to do the test due to equipment breakdown",
    "We have not been able to do the test due to electricity breakdown",
    "Other (please specify)"
  ];

  const cardReasons = [
    "Patient changed his mind",
    "Equipment breakdown preventing all tests",
    "Electricity breakdown preventing all tests",
    "Patient didn't show up for the tests",
    "Tests no longer required based on new diagnosis",
    "Other (please specify)"
  ];

  // Theme-aware styles
  const getStyles = (theme) => ({
    container: {
      fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      backgroundColor: theme.mainBg,
      minHeight: '100vh',
      padding: '40px 20px',
    },
    header: {
      color: theme.textPrimary,
      marginBottom: '30px',
      textAlign: 'center',
      fontSize: '28px',
      marginTop: '35px',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '12px',
    },
    noTestsWrapper: {
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'flex-start',
    },
    gridContainer: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
      gap: '24px',
      maxWidth: '1800px',
      margin: '0 auto',
      className: 'lab-grid',
    },
    patientCard: {
      backgroundColor: theme.cardBg,
      borderRadius: '16px',
      boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
      padding: '24px',
      position: 'relative',
      transition: 'all 0.3s ease',
      border: `1px solid ${theme.cardBorder}`,
      display: 'flex',
      flexDirection: 'column',
      height: '420px',
    },
    cardContent: {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    cardHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-start',
      marginBottom: '16px',
      paddingBottom: '12px',
      borderBottom: `2px solid ${theme.tableHeader}`,
    },
    patientInfo: {
      flex: 1,
    },
    patientName: {
      fontSize: '20px',
      fontWeight: '700',
      color: theme.textPrimary,
      marginBottom: '8px',
    },
    badge: {
      display: 'inline-block',
      backgroundColor: theme.infoLight,
      color: theme.info,
      borderRadius: '20px',
      padding: '4px 12px',
      fontSize: '13px',
      fontWeight: '600',
      marginTop: '4px',
      border: `1px solid ${theme.info}`,
    },
    loadingBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: '6px',
      backgroundColor: theme.warningLight,
      color: theme.warning,
      borderRadius: '20px',
      padding: '4px 12px',
      fontSize: '13px',
      fontWeight: '600',
      marginTop: '4px',
      border: `1px solid ${theme.warning}`,
    },
    deleteCardBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: theme.danger,
      padding: '8px',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    },
    deleteCardBtnDisabled: {
      background: 'none',
      border: 'none',
      color: theme.textMuted,
      padding: '8px',
      borderRadius: '8px',
      opacity: 0.3,
      cursor: 'not-allowed',
    },
    patientDetailsRow: {
      display: 'flex',
      gap: '16px',
      marginBottom: '16px',
      flexWrap: 'wrap',
    },
    patientDetail: {
      color: theme.textSecondary,
      fontSize: '14px',
      display: 'flex',
      alignItems: 'center',
      gap: '4px',
    },
    detailLabel: {
      fontWeight: '600',
      color: theme.textPrimary,
    },
    detailValue: {
      color: theme.textSecondary,
    },
    testSection: {
      marginTop: '12px',
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      overflow: 'hidden',
    },
    testSectionTitle: {
      fontSize: '15px',
      fontWeight: '600',
      color: theme.textPrimary,
      marginBottom: '10px',
    },
    testList: {
      listStyle: 'none',
      padding: 0,
      margin: 0,
      flex: 1,
      overflowY: 'auto',
      maxHeight: '180px',
    },
    testItem: {
      display: 'flex',
      alignItems: 'flex-start',
      padding: '10px 14px',
      marginBottom: '6px',
      backgroundColor: theme.tableHeader,
      borderRadius: '8px',
      transition: 'all 0.2s ease',
      border: `1px solid ${theme.cardBorder}`,
    },
    testNumber: {
      fontWeight: '700',
      color: theme.textMuted,
      minWidth: '24px',
      fontSize: '13px',
    },
    testName: {
      flex: 1,
      color: theme.textPrimary,
      fontSize: '13px',
      lineHeight: '1.5',
    },
    deleteTestBtn: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: theme.danger,
      padding: '4px',
      borderRadius: '4px',
      transition: 'all 0.2s ease',
      opacity: 0.6,
    },
    deleteTestBtnDisabled: {
      background: 'none',
      border: 'none',
      color: theme.textMuted,
      padding: '4px',
      borderRadius: '4px',
      opacity: 0.3,
      cursor: 'not-allowed',
    },
    submitButton: {
      backgroundColor: theme.info,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      marginTop: '16px',
      transition: 'all 0.2s ease',
      width: 'auto',
      minWidth: '140px',
      alignSelf: 'center',
    },
    submitButtonLoading: {
      backgroundColor: theme.textMuted,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '12px 20px',
      fontSize: '14px',
      fontWeight: '600',
      marginTop: '16px',
      width: 'auto',
      minWidth: '140px',
      alignSelf: 'center',
      cursor: 'not-allowed',
      opacity: 0.7,
    },
    modal: {
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 1000,
      padding: '20px',
    },
    modalContent: {
      backgroundColor: theme.cardBg,
      borderRadius: '16px',
      padding: '32px',
      maxWidth: '600px',
      width: '100%',
      maxHeight: '90vh',
      overflowY: 'auto',
      boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
      border: `1px solid ${theme.cardBorder}`,
    },
    modalTitle: {
      fontSize: '20px',
      fontWeight: '700',
      color: theme.textPrimary,
      marginBottom: '16px',
    },
    warningBox: {
      backgroundColor: theme.warningLight,
      border: `1px solid ${theme.warning}`,
      borderRadius: '8px',
      padding: '16px',
      marginBottom: '20px',
      color: theme.warning,
      fontSize: '14px',
      lineHeight: '1.6',
    },
    modalText: {
      color: theme.textSecondary,
      marginBottom: '20px',
      fontSize: '15px',
      lineHeight: '1.6',
    },
    input: {
      width: '100%',
      padding: '12px 16px',
      fontSize: '15px',
      border: `2px solid ${theme.cardBorder}`,
      borderRadius: '8px',
      marginBottom: '20px',
      fontFamily: 'inherit',
      backgroundColor: theme.cardBg,
      color: theme.textPrimary,
    },
    textarea: {
      width: '100%',
      padding: '12px 16px',
      fontSize: '15px',
      border: `2px solid ${theme.cardBorder}`,
      borderRadius: '8px',
      marginBottom: '20px',
      fontFamily: 'inherit',
      minHeight: '100px',
      resize: 'vertical',
      backgroundColor: theme.cardBg,
      color: theme.textPrimary,
    },
    modalButtons: {
      display: 'flex',
      gap: '12px',
      justifyContent: 'flex-end',
    },
    cancelBtn: {
      backgroundColor: theme.textMuted,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    confirmBtn: {
      backgroundColor: theme.danger,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    confirmBtnLoading: {
      backgroundColor: theme.danger,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'not-allowed',
      opacity: 0.7,
    },
    backBtn: {
      backgroundColor: theme.textMuted,
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      fontSize: '14px',
      fontWeight: '600',
      cursor: 'pointer',
      transition: 'all 0.2s ease',
    },
    notification: {
      position: 'fixed',
      top: '20px',
      right: '20px',
      padding: '16px 20px',
      borderRadius: '8px',
      color: 'white',
      fontWeight: '600',
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      zIndex: 1001,
      minWidth: '300px',
      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
    },
    notificationSuccess: {
      backgroundColor: theme.accent,
    },
    notificationError: {
      backgroundColor: theme.danger,
    },
    notificationWarning: {
      backgroundColor: theme.warning,
      color: '#212529',
    },
    refreshButton: {
      background: 'none',
      border: 'none',
      cursor: 'pointer',
      color: theme.info,
      padding: '8px',
      borderRadius: '8px',
      transition: 'all 0.2s ease',
    },
    loadingOverlay: {
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      borderRadius: '16px',
      zIndex: 1,
    },
    reasonOption: {
      display: 'block',
      width: '100%',
      padding: '12px 16px',
      marginBottom: '8px',
      border: `2px solid ${theme.cardBorder}`,
      borderRadius: '8px',
      backgroundColor: theme.cardBg,
      cursor: 'pointer',
      textAlign: 'left',
      fontSize: '14px',
      transition: 'all 0.2s ease',
      color: theme.textPrimary,
      fontWeight: '400',
    },
    reasonOptionSelected: {
      borderColor: theme.info,
      backgroundColor: theme.infoLight,
      color: theme.textPrimary,
      fontWeight: '500',
    },
    reasonOptionHover: {
      backgroundColor: theme.tableHeader,
      borderColor: theme.textMuted,
    },
    salesRecordQuestion: {
      marginBottom: '20px',
      padding: '16px',
      backgroundColor: theme.tableHeader,
      borderRadius: '8px',
      border: `1px solid ${theme.cardBorder}`,
    },
    radioGroup: {
      display: 'flex',
      gap: '20px',
      marginTop: '10px',
    },
    radioLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      cursor: 'pointer',
      fontSize: '14px',
      color: theme.textPrimary,
    },
  });

  const styles = getStyles(theme);

  const showNotification = (type, message) => {
    setNotification({ show: true, type, message });
    setTimeout(() => {
      setNotification({ show: false, type: '', message: '' });
    }, 5000);
  };

  const setLoading = (key, value) => {
    setLoadingStates(prev => ({ ...prev, [key]: value }));
  };

  const parseLabTests = (labTestsString) => {
    if (!labTestsString) return [];
    
    return labTestsString
      .split(';')
      .flatMap(test => test.split(','))
      .map(test => test
        .replace(/^Lab Test:\s*/i, '')
        .replace(/^HB\s+electrolysis$/i, 'HB Electrolysis')
        .trim()
      )
      .filter(test => test.length > 0)
      .map(test => {
        const standardizedTests = {
          'hb electrolysis': 'HB Electrolysis',
          'hiv/hct/rct': 'HIV/HCT/RCT',
          'blood grouping': 'Blood Grouping',
          'stool analysis': 'Stool Analysis'
        };
        
        const lowerTest = test.toLowerCase();
        return standardizedTests[lowerTest] || test;
      });
  };

  const handleDeleteTest = (patient, testIndex, testName) => {
    const tests = parseLabTests(patient.lab_tests);
    
    if (tests.length === 1) {
      showNotification('warning', 'Cannot delete the only test. Please delete the entire lab request instead.');
      return;
    }

    setConfirmDialog({
      show: true,
      type: 'test',
      data: { patient, testIndex, testName },
      step: 1
    });
    setConfirmText('');
    setDeleteSalesRecord(false);
    setDeleteReason('');
  };

  const handleDeleteCard = (patient) => {
    setConfirmDialog({
      show: true,
      type: 'card',
      data: { patient },
      step: 1
    });
    setConfirmText('');
    setDeleteSalesRecord(false);
    setDeleteReason('');
  };

  const executeDeleteTest = async () => {
    if (!deleteReason.trim()) {
      showNotification('error', 'Please provide a reason for deletion');
      return;
    }

    setLoading('deleteTest', true);
    setActionInProgress(`test-${confirmDialog.data.patient.contact_id}-${confirmDialog.data.testIndex}`);

    const { patient, testIndex } = confirmDialog.data;
    const allTests = parseLabTests(patient.lab_tests);
    const canceledTest = allTests[testIndex];
    const remainingTests = allTests.filter((_, idx) => idx !== testIndex);

    try {
      const response = await fetch(urls.cancelSpecificLabTest, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          file_id: patient.file_id,
          canceled_test: canceledTest,
          remaining_tests: remainingTests,
          delete_sales_record: deleteSalesRecord,
          deletion_reason: deleteReason,
        }),
      });

      if (response.ok) {
        // Update the specific patient's tests in place
        setPendingLabTests(prev =>
          prev.map(p => {
            if (p.contact_id === patient.contact_id) {
              const newTestsString = remainingTests.join('; ');
              return {
                ...p,
                lab_tests: newTestsString,
                totalLabTests: remainingTests.length
              };
            }
            return p;
          }).filter(p => p.totalLabTests > 0)
        );
        showNotification('success', 'Test canceled successfully');
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.message || 'Failed to cancel test');
      }
    } catch (error) {
      console.error('Error canceling test:', error);
      showNotification('error', 'Network error: Failed to cancel test');
    } finally {
      setLoading('deleteTest', false);
      setActionInProgress(null);
    }

    setConfirmDialog({ show: false, type: '', data: null, step: 1 });
    setConfirmText('');
    setDeleteSalesRecord(false);
    setDeleteReason('');
  };

  const executeDeleteCard = async () => {
    if (!deleteReason.trim()) {
      showNotification('error', 'Please provide a reason for deletion');
      return;
    }

    setLoading('deleteCard', true);
    setActionInProgress(`card-${confirmDialog.data.patient.contact_id}`);

    const { patient } = confirmDialog.data;
    const allTests = parseLabTests(patient.lab_tests);

    try {
      const response = await fetch(urls.cancelLabRequest3, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          token,
          file_id: patient.file_id,
          canceled_test: allTests,
          remaining_tests: [],
          delete_sales_record: deleteSalesRecord,
          deletion_reason: deleteReason,
        }),
      });

      if (response.ok) {
        // Remove the specific patient card
        setPendingLabTests(prev =>
          prev.filter(p => p.contact_id !== patient.contact_id)
        );
        showNotification('success', 'Lab request canceled successfully');
      } else {
        const errorData = await response.json();
        showNotification('error', errorData.message || 'Failed to cancel lab request');
      }
    } catch (error) {
      console.error('Error canceling lab request:', error);
      showNotification('error', 'Network error: Failed to cancel lab request');
    } finally {
      setLoading('deleteCard', false);
      setActionInProgress(null);
    }

    setConfirmDialog({ show: false, type: '', data: null, step: 1 });
    setConfirmText('');
    setDeleteSalesRecord(false);
    setDeleteReason('');
  };

  const handleConfirm = () => {
    if (confirmDialog.step === 1) {
      if (confirmText.toLowerCase() !== 'yes') return;
      setConfirmDialog(prev => ({ ...prev, step: 2 }));
    } else if (confirmDialog.step === 2) {
      setConfirmDialog(prev => ({ ...prev, step: 3 }));
    } else if (confirmDialog.step === 3) {
      if (confirmDialog.type === 'test') {
        executeDeleteTest();
      } else if (confirmDialog.type === 'card') {
        executeDeleteCard();
      }
    }
  };

  const handleBack = () => {
    if (confirmDialog.step === 2) {
      setConfirmDialog(prev => ({ ...prev, step: 1 }));
    } else if (confirmDialog.step === 3) {
      setConfirmDialog(prev => ({ ...prev, step: 2 }));
    }
  };

  const handleReasonSelect = (reason) => {
    setDeleteReason(reason);
  };

  const fetchClinicDetails = async (token) => {
    setLoading('fetchClinic', true);
    try {
      const response = await fetch(urls.fetchclinicdetails, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      if (response.ok) {
        const data = await response.json();
        setClinicDetails(data);
      } else {
        throw new Error('Failed to fetch clinic details');
      }
    } catch (error) {
      console.error('Error fetching clinic details:', error);
      showNotification('error', 'Failed to load clinic details');
    } finally {
      setLoading('fetchClinic', false);
    }
  };

  const fetchPendingLabTests = async (token, showRefreshing = false) => {
    if (showRefreshing) {
      setLoading('refreshing', true);
    } else {
      setLoading('fetchTests', true);
    }

    try {
      const response = await fetch(urls.pendinglabtests, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ token }),
      });
      if (response.ok) {
        const data = await response.json();

        const processedData = data.map(patient => {
          const tests = parseLabTests(patient.lab_tests);
          return {
            ...patient,
            totalLabTests: tests.length,
            lab_tests: tests.join('; ')
          };
        });

        setPendingLabTests(processedData);
        
        if (showRefreshing) {
          showNotification('success', 'Lab tests updated successfully');
        }
      } else {
        throw new Error('Failed to fetch pending lab tests data');
      }
    } catch (error) {
      console.error('Error fetching pending lab tests data:', error);
      if (showRefreshing) {
        showNotification('error', 'Failed to refresh lab tests');
      }
    } finally {
      setLoading('fetchTests', false);
      setLoading('refreshing', false);
    }
  };

  const handleManualRefresh = () => {
    fetchPendingLabTests(token, true);
  };

  useEffect(() => {
    const fetchTokenAndCheckSecurity = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const tokenFromUrl = params.get('token');
        setToken(tokenFromUrl);

        const securityResponse = await fetch(urls.security, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({ token: tokenFromUrl }),
        });

        if (securityResponse.ok) {
          const securityData = await securityResponse.json();

          if (securityData.message === 'Session valid') {
            // Check theme color from security response
            const themeColor = securityData.colour || '';
            if (!themeColor || themeColor.toLowerCase() === 'white' || themeColor.toLowerCase() === 'null') {
              setCurrentTheme('white');
            } else {
              setCurrentTheme('blue');
            }

            // Extract clinic settings from security response
            setClinicSettings({
              use_drug_expiry_date: securityData.use_drug_expiry_date || 'no',
              use_drug_batch_numbers: securityData.use_drug_batch_numbers || 'no'
            });

            await Promise.all([
              fetchClinicDetails(tokenFromUrl),
              fetchPendingLabTests(tokenFromUrl)
            ]);

            // Set up auto-refresh every 30 seconds
            const intervalId = setInterval(() => {
              fetchPendingLabTests(tokenFromUrl);
            }, 30000);

            return () => clearInterval(intervalId);
          } else if (securityData.error === 'Session expired') {
            navigate(`/dashboard?token=${securityData.clinic_session_token}`);
          } else {
            navigate('/login');
          }
        } else {
          throw new Error('Failed to perform security check');
        }
      } catch (error) {
        console.error('Error performing security check:', error);
        showNotification('error', 'Security check failed');
        navigate('/login');
      }
    };

    fetchTokenAndCheckSecurity();
  }, [navigate]);

  const handleSubmitResults = (patient) => {
    setSelectedPatient(patient);
    setShowModal(true);
    setTotalLabTests(patient.totalLabTests);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPatient(null);
    setTotalLabTests(0);
  };

  const handleSaveResults = (contactId, results) => {
    console.log('Submitting and saving results for patient ID:', contactId);
    console.log('Results:', results);
  };

  const getNotificationIcon = () => {
    switch (notification.type) {
      case 'success':
        return <CheckCircle size={20} />;
      case 'error':
        return <XCircle size={20} />;
      case 'warning':
        return <AlertTriangle size={20} />;
      default:
        return null;
    }
  };

  const getNotificationStyle = () => {
    switch (notification.type) {
      case 'success':
        return { ...styles.notification, ...styles.notificationSuccess };
      case 'error':
        return { ...styles.notification, ...styles.notificationError };
      case 'warning':
        return { ...styles.notification, ...styles.notificationWarning };
      default:
        return styles.notification;
    }
  };

  const isCardLoading = (patient, type = 'card') => {
    return actionInProgress === `${type}-${patient.contact_id}`;
  };

  const isTestLoading = (patient, testIndex) => {
    return actionInProgress === `test-${patient.contact_id}-${testIndex}`;
  };

  const renderConfirmationStep = () => {
    const { step, type, data } = confirmDialog;
    
    if (step === 1) {
      return (
        <>
          <h3 style={styles.modalTitle}>
            {type === 'test' ? '⚠️ Cancel Test' : '⚠️ Cancel Lab Request'}
          </h3>

          <div style={styles.warningBox}>
            <strong>⚠️ WARNING:</strong> This is a dangerous action that cannot be undone!
          </div>

          {type === 'test' ? (
            <p style={styles.modalText}>
              Are you sure you want to cancel the test "<strong>{data?.testName}</strong>" 
              for patient <strong>{data?.patient.first_name} {data?.patient.last_name}</strong> 
              (File ID: <strong>{data?.patient.file_id}</strong>)?
            </p>
          ) : (
            <p style={styles.modalText}>
              Are you sure you want to cancel the ENTIRE lab request 
              for patient <strong>{data?.patient.first_name} {data?.patient.last_name}</strong> 
              (File ID: <strong>{data?.patient.file_id}</strong>)?
              <br /><br />
              This will cancel all {data?.patient.totalLabTests} pending tests.
            </p>
          )}

          <input
            type="text"
            style={styles.input}
            placeholder='Type "yes" to confirm'
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            autoFocus
            disabled={loadingStates.deleteTest || loadingStates.deleteCard}
          />

          <div style={styles.modalButtons}>
            <button
              style={styles.cancelBtn}
              onClick={() => setConfirmDialog({ show: false, type: '', data: null, step: 1 })}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.textSecondary}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.textMuted}
              disabled={loadingStates.deleteTest || loadingStates.deleteCard}
            >
              Cancel
            </button>
            <button
              style={
                loadingStates.deleteTest || loadingStates.deleteCard 
                  ? styles.confirmBtnLoading 
                  : {
                      ...styles.confirmBtn,
                      opacity: confirmText.toLowerCase() === 'yes' ? 1 : 0.5,
                      cursor: confirmText.toLowerCase() === 'yes' ? 'pointer' : 'not-allowed',
                    }
              }
              onClick={handleConfirm}
              disabled={confirmText.toLowerCase() !== 'yes' || loadingStates.deleteTest || loadingStates.deleteCard}
              onMouseEnter={(e) => {
                if (confirmText.toLowerCase() === 'yes' && !loadingStates.deleteTest && !loadingStates.deleteCard) {
                  e.currentTarget.style.backgroundColor = theme.danger;
                }
              }}
              onMouseLeave={(e) => {
                if (confirmText.toLowerCase() === 'yes' && !loadingStates.deleteTest && !loadingStates.deleteCard) {
                  e.currentTarget.style.backgroundColor = theme.danger;
                }
              }}
            >
              Continue
            </button>
          </div>
        </>
      );
    } else if (step === 2) {
      return (
        <>
          <h3 style={styles.modalTitle}>Sales Record Handling</h3>
          
          <div style={styles.salesRecordQuestion}>
            <p style={styles.modalText}>
              <strong>Was the money this patient paid refunded?</strong>
              <br /><br />
              <strong>Yes:</strong> The payment record will be accounted for with a negative record. (recommended if the cashier has refunded the money)
              <br />
              <strong>No:</strong> The payment record will remain in sales records (recommended if refund was not made)
            </p>
            
            <div style={styles.radioGroup}>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="salesRecord"
                  value="yes"
                  checked={deleteSalesRecord === true}
                  onChange={() => setDeleteSalesRecord(true)}
                />
                Yes
              </label>
              <label style={styles.radioLabel}>
                <input
                  type="radio"
                  name="salesRecord"
                  value="no"
                  checked={deleteSalesRecord === false}
                  onChange={() => setDeleteSalesRecord(false)}
                />
                No
              </label>
            </div>
          </div>

          <div style={styles.modalButtons}>
            <button
              style={styles.backBtn}
              onClick={handleBack}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.textSecondary}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.textMuted}
            >
              Back
            </button>
            <button
              style={styles.confirmBtn}
              onClick={handleConfirm}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.danger}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.danger}
            >
              Continue
            </button>
          </div>
        </>
      );
    } else if (step === 3) {
      const reasons = type === 'test' ? testReasons : cardReasons;
      
      return (
        <>
          <h3 style={styles.modalTitle}>Reason for Deletion</h3>
          
          <p style={styles.modalText}>
            Please select the reason for deleting this {type === 'test' ? 'test' : 'lab request'}:
          </p>

          <div style={{ marginBottom: '20px' }}>
            {reasons.map((reason, index) => (
              <button
                key={index}
                style={{
                  ...styles.reasonOption,
                  ...(deleteReason === reason ? styles.reasonOptionSelected : {})
                }}
                onClick={() => handleReasonSelect(reason)}
                onMouseEnter={(e) => {
                  if (deleteReason !== reason) {
                    e.currentTarget.style.backgroundColor = theme.tableHeader;
                  }
                }}
                onMouseLeave={(e) => {
                  if (deleteReason !== reason) {
                    e.currentTarget.style.backgroundColor = theme.cardBg;
                  }
                }}
              >
                {reason}
              </button>
            ))}
          </div>

          <textarea
            style={styles.textarea}
            placeholder="You can edit the selected reason or provide additional details here..."
            value={deleteReason}
            onChange={(e) => setDeleteReason(e.target.value)}
            autoFocus
          />

          <div style={styles.modalButtons}>
            <button
              style={styles.backBtn}
              onClick={handleBack}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = theme.textSecondary}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = theme.textMuted}
            >
              Back
            </button>
            <button
              style={
                loadingStates.deleteTest || loadingStates.deleteCard 
                  ? styles.confirmBtnLoading 
                  : {
                      ...styles.confirmBtn,
                      opacity: deleteReason.trim() ? 1 : 0.5,
                      cursor: deleteReason.trim() ? 'pointer' : 'not-allowed',
                    }
              }
              onClick={handleConfirm}
              disabled={!deleteReason.trim() || loadingStates.deleteTest || loadingStates.deleteCard}
              onMouseEnter={(e) => {
                if (deleteReason.trim() && !loadingStates.deleteTest && !loadingStates.deleteCard) {
                  e.currentTarget.style.backgroundColor = theme.danger;
                }
              }}
              onMouseLeave={(e) => {
                if (deleteReason.trim() && !loadingStates.deleteTest && !loadingStates.deleteCard) {
                  e.currentTarget.style.backgroundColor = theme.danger;
                }
              }}
            >
              {loadingStates.deleteTest || loadingStates.deleteCard ? (
                <>
                  <Loader2 size={16} className="spin" style={{ marginRight: '8px' }} />
                  Processing...
                </>
              ) : (
                'Confirm Delete'
              )}
            </button>
          </div>
        </>
      );
    }
  };

  return (
    <>
      <GlobalStyles theme={theme} />
      <div style={styles.container}>
        <Topbar token={urlToken} themeColor={currentTheme} />
        
        <div style={styles.header}>
          <span>Pending Lab Tests</span>
          <button
            style={styles.refreshButton}
            onClick={handleManualRefresh}
            disabled={loadingStates.refreshing}
            title="Refresh lab tests"
          >
            {loadingStates.refreshing ? (
              <Loader2 size={20} className="spin" />
            ) : (
              <RefreshCw size={20} />
            )}
          </button>
        </div>

        {notification.show && (
          <div style={getNotificationStyle()}>
            {getNotificationIcon()}
            {notification.message}
          </div>
        )}

        {loadingStates.fetchTests && pendingLabTests.length === 0 ? (
          <div style={{ textAlign: 'center', color: theme.textMuted, fontSize: '18px', marginTop: '60px' }}>
            <Loader2 size={24} className="spin" style={{ marginBottom: '16px' }} />
            <div>Loading lab tests...</div>
          </div>
        ) : pendingLabTests.length === 0 ? (
          <div style={styles.noTestsWrapper}>
            <LabEmptyState theme={theme} />
          </div>
        ) : (
          <div style={styles.gridContainer}>
            {pendingLabTests.map((patient) => {
              const tests = parseLabTests(patient.lab_tests);
              const hasOnlyOneTest = tests.length === 1;
              const cardLoading = isCardLoading(patient);
              
              return (
                <div key={patient.contact_id} style={styles.patientCard}>
                  {cardLoading && (
                    <div style={styles.loadingOverlay}>
                      <Loader2 size={24} className="spin" />
                    </div>
                  )}
                  
                  <div style={styles.cardContent}>
                    <div style={styles.cardHeader}>
                      <div style={styles.patientInfo}>
                        <h2 style={styles.patientName}>
                          {`${patient.first_name} ${patient.last_name}`}
                        </h2>
                        {cardLoading ? (
                          <span style={styles.loadingBadge}>
                            <Loader2 size={12} className="spin" />
                            Processing...
                          </span>
                        ) : (
                          <span style={styles.badge}>{patient.totalLabTests} tests</span>
                        )}
                      </div>
                      <button
                        style={cardLoading ? styles.deleteCardBtnDisabled : styles.deleteCardBtn}
                        onClick={cardLoading ? undefined : () => handleDeleteCard(patient)}
                        onMouseEnter={(e) => {
                          if (!cardLoading) {
                            e.currentTarget.style.backgroundColor = theme.dangerLight;
                            e.currentTarget.style.transform = 'scale(1.1)';
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!cardLoading) {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.transform = 'scale(1)';
                          }
                        }}
                        title={cardLoading ? "Processing..." : "Delete entire lab request"}
                        disabled={cardLoading}
                      >
                        {cardLoading ? <Loader2 size={20} className="spin" /> : <Trash2 size={20} />}
                      </button>
                    </div>

                    <div style={styles.patientDetailsRow}>
                      <div style={styles.patientDetail}>
                        <span style={styles.detailLabel}>File ID:</span>
                        <span style={styles.detailValue}>{patient.file_id}</span>
                      </div>
                      <div style={styles.patientDetail}>
                        <span style={styles.detailLabel}>Age:</span>
                        <span style={styles.detailValue}>{patient.age} yrs</span>
                      </div>
                      <div style={styles.patientDetail}>
                        <span style={styles.detailLabel}>Sex:</span>
                        <span style={styles.detailValue}>{patient.sex}</span>
                      </div>
                    </div>

                    <div style={styles.testSection}>
                      <p style={styles.testSectionTitle}>Tests to be done:</p>
                      <ul style={styles.testList}>
                        {tests.map((test, index) => {
                          const testLoading = isTestLoading(patient, index);
                          const testDisabled = hasOnlyOneTest || testLoading || cardLoading;
                          
                          return (
                            <li
                              key={index}
                              style={{
                                ...styles.testItem,
                                opacity: testLoading ? 0.6 : 1
                              }}
                              onMouseEnter={(e) => {
                                if (!testLoading) {
                                  e.currentTarget.style.backgroundColor = theme.cardBorder;
                                }
                              }}
                              onMouseLeave={(e) => {
                                if (!testLoading) {
                                  e.currentTarget.style.backgroundColor = theme.tableHeader;
                                }
                              }}
                            >
                              <span style={styles.testNumber}>{index + 1}.</span>
                              <span style={styles.testName}>
                                {test}
                                {testLoading && (
                                  <Loader2 size={12} className="spin" style={{ marginLeft: '8px' }} />
                                )}
                              </span>
                              <button
                                style={testDisabled ? styles.deleteTestBtnDisabled : styles.deleteTestBtn}
                                onClick={testDisabled ? undefined : () => handleDeleteTest(patient, index, test)}
                                onMouseEnter={(e) => {
                                  if (!testDisabled) {
                                    e.currentTarget.style.opacity = '1';
                                    e.currentTarget.style.transform = 'scale(1.2) rotate(10deg)';
                                  }
                                }}
                                onMouseLeave={(e) => {
                                  if (!testDisabled) {
                                    e.currentTarget.style.opacity = '0.6';
                                    e.currentTarget.style.transform = 'scale(1) rotate(0deg)';
                                  }
                                }}
                                title={
                                  testLoading ? "Processing..." :
                                  hasOnlyOneTest ? "Cannot delete the only test. Delete entire request instead." : 
                                  cardLoading ? "Action in progress..." :
                                  "Cancel this test"
                                }
                                disabled={testDisabled}
                              >
                                {testLoading ? <Loader2 size={16} className="spin" /> : <Trash2 size={16} />}
                              </button>
                            </li>
                          );
                        })}
                      </ul>
                    </div>

                    <button
                      style={cardLoading ? styles.submitButtonLoading : styles.submitButton}
                      onMouseEnter={(e) => {
                        if (!cardLoading) {
                          e.currentTarget.style.backgroundColor = theme.skyBlue;
                          e.currentTarget.style.transform = 'translateY(-2px)';
                          e.currentTarget.style.boxShadow = `0 4px 12px ${theme.info}`;
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!cardLoading) {
                          e.currentTarget.style.backgroundColor = theme.info;
                          e.currentTarget.style.transform = 'translateY(0)';
                          e.currentTarget.style.boxShadow = 'none';
                        }
                      }}
                      onClick={cardLoading ? undefined : () => handleSubmitResults(patient)}
                      disabled={cardLoading}
                    >
                      {cardLoading ? (
                        <>
                          <Loader2 size={16} className="spin" style={{ marginRight: '8px' }} />
                          Processing...
                        </>
                      ) : (
                        'Enter Results'
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {showModal && selectedPatient && (
          <TestResultModal
            patient={selectedPatient}
            clinicDetails={clinicDetails}
            token={token}
            totalLabTests={totalLabTests}
            labTests={selectedPatient.lab_tests}
            useDrugExpiryDate={clinicSettings.use_drug_expiry_date}
            useDrugBatchNumbers={clinicSettings.use_drug_batch_numbers}
            onClose={handleCloseModal}
            onSubmit={handleSaveResults}
          />
        )}

        {confirmDialog.show && (
          <div style={styles.modal} onClick={() => setConfirmDialog({ show: false, type: '', data: null, step: 1 })}>
            <div style={styles.modalContent} onClick={(e) => e.stopPropagation()}>
              {renderConfirmationStep()}
            </div>
          </div>
        )}
      </div>
    </>
  );
}

export default Laboratory;